import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  CardHeader,
  Combobox,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  CHOICE_SOURCE,
  LINE_STATUS,
  LINE_STATUS_LABELS,
  LINE_STATUS_TONES,
  OPEN_LINE_STATUSES,
  editInvoiceLine,
  lineLabel,
  listExpenseCategories,
  type ExpenseCategory,
  type InvoiceLineStatus,
  type CandidateEvent,
  CONTRACT_MATCH_LABELS,
  CONTRACT_MATCH_REASON,
  CONTRACT_MATCH_REASON_TEXT,
  CONTRACT_MATCH_STATUS,
  CONTRACT_MATCH_TONES,
  INVOICES_PATH,
  INVOICE_STATUS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
  READING_POLL_MS,
  fetchInvoiceFile,
  describeEvent,
  invoicePath,
  matchInvoiceAgain,
  readInvoice,
  readInvoiceAgain,
  type InvoiceDocument,
  type InvoiceLine,
} from '@/lib/invoices';
import { formatMoney, formatShortDate } from '@/proposals/lib/money';
import { salesforceExpenseUrl, salesforceOpportunityUrl, salesforceStatementUrl } from '@/lib/salesforce';
import { ExternalLinkIcon } from 'lucide-react';
import { INVOICES_QUERY_KEY } from '@/pages/InvoicesPage';
import {
  ApproveDialog,
  ContractPicker,
  HoldDialog,
  LineActions,
  SplitDialog,
  VendorPicker,
  approveOutcome,
  type LineDialog,
} from '@/pages/invoice-review';
import type { ApproveResult } from '@/lib/invoices';

/** A gap under a cent is rounding, not a missing line. */
const TOTALS_TOLERANCE = 0.01;

function invoiceQueryKey(id: string) {
  return [...INVOICES_QUERY_KEY, id] as const;
}

/**
 * An object URL for a blob, revoked when the blob changes or the page goes.
 * The original needs the staff token, so it is fetched, not linked.
 */
function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return undefined;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function period(document: InvoiceDocument): string {
  if (!document.periodStart && !document.periodEnd) return '—';
  return `${formatShortDate(document.periodStart)} to ${formatShortDate(document.periodEnd)}`;
}

/** The original, beside the lines: a PDF in a frame, a photo as an image. */
function OriginalFile({ document }: { readonly document: InvoiceDocument }): React.ReactElement {
  const file = useQuery({
    queryKey: [...invoiceQueryKey(document.id), 'file'],
    queryFn: ({ signal }) => fetchInvoiceFile(document.id, signal),
    staleTime: Infinity,
  });
  const url = useObjectUrl(file.data);

  if (file.isError) return <ErrorNotice inline message={`The original couldn't load: ${errorMessage(file.error)}`} />;
  if (!url) return <LoadingState label="Loading the original" />;
  return document.fileType === 'application/pdf' ? (
    <iframe src={url} title={`Original: ${document.fileName}`} className="h-[60vh] w-full rounded-portal-card border border-portal-line" />
  ) : (
    <img src={url} alt={`Original invoice ${document.fileName}`} className="max-h-[60vh] w-full rounded-portal-card border border-portal-line object-contain object-top" />
  );
}

/** What the contract cell says: the line's contract (and how it got there), or what matching found. */
function ContractSummary({ line }: { readonly line: InvoiceLine }): React.ReactElement {
  const match = line.contractMatch;
  // What the vendor wrote, whenever it differs from the contract shown, so a
  // mismatch is never hidden behind a match.
  const invoiceSaid =
    line.contractNumber && line.contractNumber !== line.assignment?.contractNumber ? (
      <span className="block text-portal-micro text-portal-muted">Invoice: {line.contractNumber}</span>
    ) : null;
  if (line.assignment) {
    const chosen = line.assignmentSource === CHOICE_SOURCE.chosen;
    return (
      <>
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span className="font-bold">{line.assignment.contractNumber ?? 'No contract #'}</span>
          {chosen ? (
            <Pill tone="brand">Chosen</Pill>
          ) : match ? (
            <Pill tone={CONTRACT_MATCH_TONES[match.status]}>{CONTRACT_MATCH_LABELS[match.status]}</Pill>
          ) : null}
        </span>
        <span className="block text-portal-micro text-portal-muted">{describeEvent(line.assignment)}</span>
        {invoiceSaid}
      </>
    );
  }
  if (!match) return <span className="text-portal-muted">{line.contractNumber ?? 'Not matched yet'}</span>;
  if (match.reason === CONTRACT_MATCH_REASON.contractNotFound) {
    return (
      <>
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span className="font-bold">{line.contractNumber}</span>
          <Pill tone="warning">Not in BCE Cloud</Pill>
        </span>
        <span className="block text-portal-micro text-portal-muted">
          {match.candidates.length > 0
            ? `${match.candidates.length} other ${match.candidates.length === 1 ? 'event' : 'events'} around that date`
            : 'No event has this contract number'}
        </span>
      </>
    );
  }
  return (
    <>
      <Pill tone={CONTRACT_MATCH_TONES[match.status]}>{CONTRACT_MATCH_LABELS[match.status]}</Pill>
      <span className="block text-portal-micro text-portal-muted">
        {match.status === CONTRACT_MATCH_STATUS.ambiguous && match.candidates.length > 0
          ? candidatesSummary(match.candidates)
          : line.contractNumber
            ? `${line.contractNumber} on the invoice`
            : 'No contract on the invoice'}
      </span>
      {match.status === CONTRACT_MATCH_STATUS.ambiguous ? invoiceSaid : null}
    </>
  );
}

/** The contract cell: a button that opens the picker while the line is open. */
function ContractCell({ line, onPick }: { readonly line: InvoiceLine; readonly onPick: () => void }): React.ReactElement {
  const reason = line.contractMatch ? CONTRACT_MATCH_REASON_TEXT[line.contractMatch.reason] : undefined;
  if (!OPEN_LINE_STATUSES.includes(line.status)) {
    return (
      <div title={reason}>
        <ContractSummary line={line} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onPick}
      title={reason}
      aria-label={`Change the contract for line ${lineLabel(line)}`}
      className="-m-1 rounded-md p-1 text-left hover:bg-portal-sky"
    >
      <ContractSummary line={line} />
      {line.assignment ? null : <span className="block text-portal-micro font-bold text-primary">Choose contract</span>}
    </button>
  );
}

/**
 * "2 possible: 9700-BC, 9701-BC", or "3 events under 9687-BC" when they share
 * one contract number (a series), so the list never repeats itself.
 */
export function candidatesSummary(candidates: readonly CandidateEvent[]): string {
  const numbers = [...new Set(candidates.map((event) => event.contractNumber ?? event.accountName ?? 'an event'))];
  if (numbers.length === 1 && candidates.length > 1) return `${candidates.length} events under ${numbers[0]}`;
  return `${candidates.length} possible: ${numbers.slice(0, 2).join(', ')}${numbers.length > 2 ? '…' : ''}`;
}

/** A link that opens a Salesforce record in a new tab. */
function SalesforceLink({ href, children }: { readonly href: string; readonly children: React.ReactNode }): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1 inline-flex items-center gap-1 text-portal-micro font-bold text-primary hover:underline"
    >
      {children}
      <ExternalLinkIcon className="size-3" aria-hidden />
      <span className="sr-only">(opens Salesforce in a new tab)</span>
    </a>
  );
}

/**
 * Where a matched line's money lands in Salesforce: the opportunity's Income &
 * Expense Statement, or the opportunity itself until it has one (the statement
 * is created on the first post).
 */
function StatementLink({ line, statements }: { readonly line: InvoiceLine; readonly statements: Readonly<Record<string, string>> }): React.ReactElement | null {
  const opportunityId = line.assignment?.opportunityId;
  if (!opportunityId) return null;
  const statementId = line.statementId ?? statements[opportunityId];
  return statementId ? (
    <SalesforceLink href={salesforceStatementUrl(statementId)}>Expense statement</SalesforceLink>
  ) : (
    <SalesforceLink href={salesforceOpportunityUrl(opportunityId)}>Opportunity</SalesforceLink>
  );
}

/** What a line still needs before it can post, in the reviewer's words. */
export function nextStep(line: Pick<InvoiceLine, 'assignment' | 'categoryId'>): string {
  if (!line.assignment) return 'Choose the contract';
  if (!line.categoryId) return 'Choose a category';
  return 'Ready once both are set';
}

const SUMMARY_ORDER: readonly InvoiceLineStatus[] = [
  LINE_STATUS.posted,
  LINE_STATUS.ready,
  LINE_STATUS.needsReview,
  LINE_STATUS.quarantined,
  LINE_STATUS.excluded,
];

/** "4 Ready · 1 Needs review · 1 Held": where the review stands. */
function LineSummary({ lines }: { readonly lines: readonly InvoiceLine[] }): React.ReactElement | null {
  const counts = new Map<InvoiceLineStatus, number>();
  for (const line of lines) counts.set(line.status, (counts.get(line.status) ?? 0) + 1);
  const shown = SUMMARY_ORDER.filter((status) => counts.has(status));
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Line review">
      {shown.map((status) => (
        <Pill key={status} tone={LINE_STATUS_TONES[status]}>
          {counts.get(status)} {LINE_STATUS_LABELS[status]}
        </Pill>
      ))}
    </div>
  );
}

/** A line's category: a picker over the Salesforce list while open. */
function CategoryCell({
  line,
  categories,
  onChange,
}: {
  readonly line: InvoiceLine;
  readonly categories: readonly ExpenseCategory[];
  readonly onChange: (categoryId: string | null) => void;
}): React.ReactElement {
  if (!OPEN_LINE_STATUSES.includes(line.status) || categories.length === 0) {
    return line.category ? <>{line.category}</> : <span className="text-portal-muted">Not set</span>;
  }
  return (
    <div className="min-w-44">
      <Combobox
        options={categories.map((category) => ({ value: category.id, label: category.name }))}
        value={line.categoryId ?? ''}
        onChange={(value) => onChange(value === '' ? null : value)}
        noneLabel="Not set"
        placeholder="Choose a category"
      />
    </div>
  );
}

/**
 * Tools → Invoices → one invoice: the original next to what was read from it.
 * Phase 1 is read-only; matching, review and posting come next.
 *
 * @returns The invoice page.
 */
export function InvoiceDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const invoice = useQuery({
    queryKey: invoiceQueryKey(id),
    queryFn: ({ signal }) => readInvoice(id, signal),
    refetchInterval: (query) => (query.state.data?.document.status === INVOICE_STATUS.parsing ? READING_POLL_MS : false),
  });

  const readAgain = useMutation({
    mutationFn: () => readInvoiceAgain(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY }),
  });

  const matchAgain = useMutation({
    mutationFn: () => matchInvoiceAgain(id),
    onSuccess: (detail) => queryClient.setQueryData(invoiceQueryKey(id), detail),
  });

  // Split lines are replaced by their parts; the parts carry the review.
  const lines = useMemo(() => (invoice.data?.lines ?? []).filter((line) => line.status !== LINE_STATUS.split), [invoice.data]);
  const [dialog, setDialog] = useState<LineDialog | null>(null);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [lastApproval, setLastApproval] = useState<ApproveResult | null>(null);
  const categories = useQuery({ queryKey: ['invoice-categories'], queryFn: ({ signal }) => listExpenseCategories(signal), staleTime: 10 * 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: invoiceQueryKey(id) });
  const afterDialog = () => {
    setDialog(null);
    void refresh();
  };
  const setCategory = useMutation({
    mutationFn: ({ lineId, categoryId }: { lineId: string; categoryId: string | null }) => editInvoiceLine(lineId, { categoryId }),
    onSuccess: () => refresh(),
  });

  if (invoice.isPending) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="TOOLS" title="Invoice" backTo={INVOICES_PATH} />
        <SkeletonTable />
      </section>
    );
  }
  if (invoice.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="TOOLS" title="Invoice" backTo={INVOICES_PATH} />
        <ErrorNotice title="Invoice unavailable" message={errorMessage(invoice.error)} />
      </section>
    );
  }

  const { document } = invoice.data;
  const reading = document.status === INVOICE_STATUS.parsing;
  const canReadAgain = document.status === INVOICE_STATUS.failed || document.status === INVOICE_STATUS.needsReview;
  const gap = document.totalsGap;
  const readyCount = lines.filter((line) => line.status === LINE_STATUS.ready).length;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="TOOLS"
        title={document.vendorName ?? document.fileName}
        subtitle={document.invoiceNumber ? `Invoice ${document.invoiceNumber}` : document.fileName}
        backTo={INVOICES_PATH}
        action={
          canReadAgain ? (
            <div className="flex flex-wrap gap-2">
              {document.status === INVOICE_STATUS.needsReview ? (
                <Button variant="secondary" onClick={() => matchAgain.mutate()} disabled={matchAgain.isPending}>
                  {matchAgain.isPending ? 'Matching…' : 'Match again'}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => readAgain.mutate()} disabled={readAgain.isPending}>
                Read again
              </Button>
            </div>
          ) : undefined
        }
      />

      {readAgain.isError || matchAgain.isError ? (
        <div className="mb-4">
          <ErrorNotice inline message={errorMessage(readAgain.error ?? matchAgain.error)} />
        </div>
      ) : null}
      {document.status === INVOICE_STATUS.failed && document.error ? (
        <div className="mb-4">
          <ErrorNotice title="We couldn't read this invoice" message={document.error} />
        </div>
      ) : null}
      {document.duplicateOfId ? (
        <div className="mb-4">
          <ErrorNotice
            inline
            message="This vendor's invoice with the same number was dropped before. Check it isn't posted twice."
          />
          <Link to={invoicePath(document.duplicateOfId)} className="text-portal-small font-bold text-primary hover:underline">
            Open the earlier copy
          </Link>
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <Panel className="p-5">
          <CardHeader title="Original" description={document.fileName} />
          <OriginalFile document={document} />
        </Panel>

        <div className="flex min-w-0 flex-col gap-4">
          <Panel className="p-5">
            <CardHeader
              title="Invoice"
              description="What was read from the header."
              aside={<Pill tone={INVOICE_STATUS_TONES[document.status]}>{INVOICE_STATUS_LABELS[document.status]}</Pill>}
            />
            <FactList
              columns={3}
              phoneColumns={2}
              facts={[
                {
                  label: 'Vendor',
                  value: (
                    <>
                      {document.vendorName ?? '—'}
                      <span className="block text-portal-micro text-portal-muted">
                        {document.vendorAccountName ? `In Salesforce as ${document.vendorAccountName}` : 'No Salesforce vendor matched yet'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setVendorOpen(true)}
                        className="text-portal-micro font-bold text-primary hover:underline"
                      >
                        {document.vendorAccountName ? 'Change vendor' : 'Choose vendor'}
                      </button>
                    </>
                  ),
                },
                { label: 'Invoice #', value: document.invoiceNumber ?? '—' },
                { label: 'Total', value: document.total === null ? '—' : formatMoney(document.total) },
                { label: 'Invoice date', value: formatShortDate(document.invoiceDate) },
                { label: 'Due', value: formatShortDate(document.dueDate) },
                { label: 'Period', value: period(document) },
              ]}
            />
            {document.unassignedContractNumbers.length > 0 ? (
              <div className="mt-3">
                <ErrorNotice
                  inline
                  message={`The invoice mentions ${document.unassignedContractNumbers.join(', ')} but no line got ${document.unassignedContractNumbers.length === 1 ? 'it' : 'them'}. Check the original and choose the contract on the line it belongs to.`}
                />
              </div>
            ) : null}
            {gap !== null && Math.abs(gap) >= TOTALS_TOLERANCE ? (
              <div className="mt-3">
                <ErrorNotice
                  inline
                  message={`The lines add up to ${formatMoney((document.total ?? 0) - gap)}, ${formatMoney(Math.abs(gap))} ${gap > 0 ? 'less' : 'more'} than the invoice total. A line may be missing or read twice.`}
                />
              </div>
            ) : null}
          </Panel>

        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
            {reading ? (
              <Panel className="p-5">
                <LoadingState label="Reading the invoice. This takes about half a minute." />
              </Panel>
            ) : lines.length === 0 ? (
              <Panel>
                <EmptyState
                  title="No lines read"
                  message="Nothing on this invoice was read as a charge. Read it again, or check the original."
                />
              </Panel>
            ) : (
              <>
              <div className="flex flex-wrap items-center justify-between gap-3">
              <LineSummary lines={lines} />
              <div className="flex items-center gap-3">
                {readyCount > 0 && !document.vendorAccountId ? (
                  <span className="text-portal-micro text-portal-muted">Choose the vendor to post</span>
                ) : null}
                <Button onClick={() => setApproveOpen(true)} disabled={readyCount === 0 || !document.vendorAccountId}>
                  {readyCount > 0 ? `Post ${readyCount} to Salesforce` : 'Nothing ready to post'}
                </Button>
              </div>
            </div>
            {lastApproval ? (
              approveOutcome(lastApproval).problem ? (
                <ErrorNotice title={approveOutcome(lastApproval).message} message={approveOutcome(lastApproval).problem ?? ''} />
              ) : (
                <p role="status" className="rounded-portal-card bg-portal-success-bg p-3 text-portal-small text-portal-success">
                  {approveOutcome(lastApproval).message}
                </p>
              )
            ) : null}
            {document.qboError && !lastApproval ? (
              <ErrorNotice inline message={`The last QuickBooks Bill wasn't created: ${document.qboError}`} />
            ) : null}
              {setCategory.isError ? <ErrorNotice inline message={errorMessage(setCategory.error)} /> : null}
              <DataTable
                headers={[
                  { label: '#' },
                  { label: 'Date' },
                  { label: 'Charge' },
                  { label: 'Contract' },
                  { label: 'Category' },
                  { label: 'Amount' },
                  { label: 'Status' },
                  { label: '' },
                ]}
                rightAlignedHeaders={[5]}
              >
                {lines.map((line) => (
                  <tr
                    key={line.id}
                    className={`border-t border-portal-line align-middle ${OPEN_LINE_STATUSES.includes(line.status) ? '' : 'opacity-70'}`}
                  >
                    <td className="p-3 text-portal-small text-portal-muted">{lineLabel(line)}</td>
                    <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{formatShortDate(line.serviceDate)}</td>
                    <td className="p-3 text-portal-small">
                      {line.description}
                      {line.eventReference && !line.description.includes(line.eventReference) ? (
                        <span className="block text-portal-micro text-portal-muted">{line.eventReference}</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-portal-small">
                      <ContractCell line={line} onPick={() => setDialog({ kind: 'contract', line })} />
                      <StatementLink line={line} statements={invoice.data.statements ?? {}} />
                    </td>
                    <td className="p-3 text-portal-small whitespace-nowrap">
                      <CategoryCell
                        line={line}
                        categories={categories.data ?? []}
                        onChange={(categoryId) => setCategory.mutate({ lineId: line.id, categoryId })}
                      />
                    </td>
                    <td className="p-3 text-portal-small text-right whitespace-nowrap">{formatMoney(line.amount)}</td>
                    <td className="p-3 text-portal-small">
                      <span className="whitespace-nowrap">
                        <Pill tone={LINE_STATUS_TONES[line.status]}>{LINE_STATUS_LABELS[line.status]}</Pill>
                      </span>
                      {line.status === LINE_STATUS.needsReview ? (
                        <button
                          type="button"
                          onClick={() => (line.assignment ? undefined : setDialog({ kind: 'contract', line }))}
                          className={`block text-left text-portal-micro ${line.assignment ? 'text-portal-muted' : 'font-bold text-primary hover:underline'}`}
                        >
                          {nextStep(line)}
                        </button>
                      ) : null}
                      {line.holdReason ? <span className="block text-portal-micro text-portal-muted">{line.holdReason}</span> : null}
                      {line.postError && line.status === LINE_STATUS.ready ? (
                        <span className="block text-portal-micro text-rose-700">Not posted: {line.postError}</span>
                      ) : null}
                      {line.status === LINE_STATUS.posted ? (
                        line.expenseId ? (
                          <SalesforceLink href={salesforceExpenseUrl(line.expenseId)}>
                            {line.qboBillId ? 'In Salesforce and QuickBooks' : 'In Salesforce'}
                          </SalesforceLink>
                        ) : (
                          <span className="block text-portal-micro text-portal-muted">{line.qboBillId ? 'In Salesforce and QuickBooks' : 'In Salesforce'}</span>
                        )
                      ) : null}
                    </td>
                    <td className="p-3 text-portal-small text-right">
                      <LineActions line={line} onOpen={setDialog} onReleased={() => void refresh()} />
                    </td>
                  </tr>
                ))}
              </DataTable>
              </>
            )}
      </div>

      {dialog?.kind === 'contract' ? <ContractPicker line={dialog.line} onClose={() => setDialog(null)} onSaved={afterDialog} /> : null}
      {dialog?.kind === 'split' ? <SplitDialog line={dialog.line} onClose={() => setDialog(null)} onSaved={afterDialog} /> : null}
      {dialog?.kind === 'hold' ? (
        <HoldDialog line={dialog.line} action={dialog.action} onClose={() => setDialog(null)} onSaved={afterDialog} />
      ) : null}
      {approveOpen ? (
        <ApproveDialog
          documentId={document.id}
          vendorName={document.vendorAccountName ?? document.vendorName ?? 'this vendor'}
          lines={lines}
          onClose={() => setApproveOpen(false)}
          onDone={(result) => {
            setApproveOpen(false);
            setLastApproval(result);
            void refresh();
            void queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
          }}
        />
      ) : null}
      {vendorOpen ? (
        <VendorPicker
          documentId={document.id}
          invoiceVendorName={document.vendorName}
          hasVendor={document.vendorAccountId !== null}
          onClose={() => setVendorOpen(false)}
          onSaved={() => {
            setVendorOpen(false);
            void refresh();
          }}
        />
      ) : null}
    </section>
  );
}
